import React, { useState, useEffect } from 'react';
import Pager from '../../components/Pager';
import EditablePageText from '../../components/EditablePageText';
import { isOperator } from '../../utils/permission';
import UploadCounter from './UploadCounter';
import TacticPost from './TacticPost';
import { getMonsterImgPath } from '../../utils/monster';
import Loading from '../../components/Loading';
import ProfileWithName from '../../components/ProfileWithName';
import { loadMonsterMapping } from '../../utils/monsterMapping';
import { useBackHandler } from '../../hooks/useBackHandler';

interface StrategyProps {
  externalSelectedTactic?: any;
  onExternalTacticClose?: () => void;
}

const Strategy: React.FC<StrategyProps> = ({ externalSelectedTactic, onExternalTacticClose }) => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  /**
   * 다른 화면의 [공략 작성하러 가기] — 이 탭의 작성 창을 그 방덱을 채워 연다(탭 전환은 PageContainer 가 한다).
   */
  const [writeDefense, setWriteDefense] = useState<any[] | null>(null);
  useEffect(() => {
    const on = (e: Event) => {
      const team = (e as CustomEvent).detail?.defenseTeam;
      setWriteDefense(Array.isArray(team) && team.length === 3 ? team : null);
      setUploadModalOpen(true);
    };
    window.addEventListener('swpvpdb:write-tactic', on);
    return () => window.removeEventListener('swpvpdb:write-tactic', on);
  }, []);
  const [user, setUser] = useState<{ uuid: string; nickname: string; role: string; profileImg: string; siteAdmin?: boolean } | null>(null);
  const [tactics, setTactics] = useState<any[]>([]);
  const [selectedTactic, setSelectedTactic] = useState<any>(null);
  const [monsterMapping, setMonsterMapping] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // 유저 정보 가져오기
  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setUser({ uuid: data.uuid, nickname: data.nickname, role: data.role, profileImg: data.profileImg || '', siteAdmin: !!data.siteAdmin });
        }
      })
      .catch(err => {});
  }, []);

  // 몬스터 매핑 가져오기
  useEffect(() => {
    loadMonsterMapping()
      .then(data => setMonsterMapping(data))
      .catch(err => {});
  }, []);

  // 공략 목록 가져오기
  const fetchTactics = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/tactics');
      if (response.ok) {
        const data = await response.json();
        setTactics(data);
      }
    } catch (error) {
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchTactics();
  }, []);

  // 외부에서 전달된 공략이 있으면 자동으로 열기
  useEffect(() => {
    if (externalSelectedTactic) {
      setSelectedTactic(externalSelectedTactic);
    }
  }, [externalSelectedTactic]);

  const handleTacticClick = async (tactic: any) => {
    // 조회수 증가
    try {
      await fetch('/api/tactics/view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: tactic._id })
      });
    } catch (error) {
    }

    setSelectedTactic(tactic);
  };

  // 뒤로 가기: 공략 상세 창 · 공략 쓰기 창 닫기 (각 창의 닫기와 같다)
  useBackHandler(!!(selectedTactic && monsterMapping && user), () => {
    setSelectedTactic(null);
    if (onExternalTacticClose) {
      onExternalTacticClose();
    }
  });
  useBackHandler(uploadModalOpen && !!user, () => { setUploadModalOpen(false); setWriteDefense(null); });

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      paddingBottom: 60
    }}>
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '40px 32px'
      }}>
        {/* 헤더 */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 24,
          padding: '40px 32px 32px',
          marginBottom: 32,
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: '100%',
          position: 'relative',
          zIndex: 100
        }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <h1 style={{
              color: '#333',
              fontSize: 40,
              fontWeight: 900,
              margin: 0,
              marginBottom: 12,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-1px'
            }}>공략</h1>
            <EditablePageText pageKey="strategy" defaultText="길드 점령전 공략 게시글입니다" canEdit={isOperator(user)} style={{
              color: '#222',
              fontSize: 17,
              margin: 0,
              fontWeight: 600
            }} />
          </div>
        </div>

        {/* 공략 목록 */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 24,
          padding: '32px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>

          {isLoading ? (
            <Loading padding={60} />
          ) : tactics.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
              등록된 공략이 없습니다
            </div>
          ) : (
            <div>
              {(() => {
                const totalPages = Math.ceil(tactics.length / itemsPerPage);
                const startIdx = (currentPage - 1) * itemsPerPage;
                const endIdx = startIdx + itemsPerPage;
                const currentTactics = tactics.slice(startIdx, endIdx);

                return (
                  <>
              {/* 머리줄 — 공지 탭처럼 번호·제목…(사용자 2026-10-30). 칸 폭은 아래 줄과 같다 */}
              <div style={{
                display: 'flex', alignItems: 'center', height: 34, fontSize: 12, fontWeight: 700, color: '#8a8fb5',
                borderBottom: '1.5px solid rgba(102, 126, 234, 0.45)'
              }}>
                <span style={{ width: 48, flexShrink: 0, textAlign: 'center' }}>번호</span>
                <span style={{ flex: 1, minWidth: 0, paddingLeft: 12, textAlign: 'left' }}>제목</span>
                <span style={{ width: 280, flexShrink: 0, paddingLeft: 8, textAlign: 'left' }}>공방덱</span>
                <span style={{ width: 160, flexShrink: 0, paddingLeft: 8, textAlign: 'left' }}>작성자</span>
                <span style={{ width: 64, flexShrink: 0, textAlign: 'center' }}>작성일</span>
                <span style={{ width: 72, flexShrink: 0, paddingLeft: 8, textAlign: 'left' }}>조회</span>
              </div>
              {currentTactics.map((tactic, idx) => (
                <div
                  key={tactic._id}
                  style={{
                    borderBottom: idx === currentTactics.length - 1 ? '2px solid rgba(102, 126, 234, 0.25)' : '1px solid rgba(102, 126, 234, 0.3)',
                    background: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.8)' : 'rgba(248, 250, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 60,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    position: 'relative'
                  }}
                  onClick={() => handleTacticClick(tactic)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = idx % 2 === 0 ? 'rgba(255, 255, 255, 0.8)' : 'rgba(248, 250, 255, 0.8)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* 1. 순번 */}
                  {/*
                    칸 사이 세로줄은 없앴다. 제목만 남는 폭을 다 쓰고 몹·작성자·시간·조회수는 오른쪽에 **고정 폭**으로 붙인다 —
                    글마다 몹 상자·작성자의 x 가 어긋나지 않게(사용자 2026-10-30)
                  */}
                  <div style={{
                    width: 48,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#667eea'
                  }}>
                    {startIdx + idx + 1}
                  </div>

                  {/* 2. 제목 */}
                  <div style={{
                    flex: 1,
                    minWidth: 0,
                    paddingLeft: 12,
                    textAlign: 'left',
                    paddingRight: 16,
                    fontSize: 15,
                    fontWeight: 400,
                    color: '#333',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {tactic.title}
                  </div>

                  {/* 3. 공덱과 방덱 */}
                  <div style={{
                    width: 280,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 6,
                    padding: '0 8px'
                  }}>
                    {/* 공덱 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        background: '#fff',
                        padding: '4px 6px',
                        borderRadius: 8,
                        border: '2px solid #1976d2',
                        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)'
                      }}>
                        {monsterMapping && Array.from({ length: 3 }, (_, idx) => {
                          const m = (tactic.attackTeam || [])[idx] || { name: '', attribute: '' };
                          const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                          return (
                            <img loading="lazy"
                              key={idx}
                              src={imgPath ? `/mobs/v3/${imgPath.replace(/\\/g, '/')}` : `/images/empty.png`}
                              alt={displayName}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 4,
                                objectFit: 'contain',
                                display: 'inline-block',
                                flexShrink: 0
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {/* VS + 방덱 (방덱이 있을 때만) */}
                    {tactic.defenseTeam && tactic.defenseTeam.length > 0 && tactic.defenseTeam.some((m: any) => m.name) ? (
                      <>
                        <span style={{
                          color: '#667eea',
                          fontWeight: 600,
                          fontSize: 20,
                          letterSpacing: '1.5px',
                          flexShrink: 0
                        }}>
                          VS
                        </span>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            background: '#fff',
                            padding: '4px 6px',
                            borderRadius: 8,
                            border: '2px solid #d32f2f',
                            boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)'
                          }}>
                            {monsterMapping && Array.from({ length: 3 }, (_, idx) => {
                              const m = (tactic.defenseTeam || [])[idx] || { name: '', attribute: '' };
                              const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                              return (
                                <img loading="lazy"
                                  key={idx}
                                  src={imgPath ? `/mobs/v3/${imgPath.replace(/\\/g, '/')}` : `/images/empty.png`}
                                  alt={displayName}
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 4,
                                    objectFit: 'contain',
                                    display: 'inline-block',
                                    flexShrink: 0
                                  }}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </>
                    ) : null}
                  </div>

                  {/* 4. 글쓴이 */}
                  <div style={{
                    width: 160,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingLeft: 8,
                    paddingRight: 8
                  }}>
                    <div style={{ width: '100%', overflow: 'hidden' }}>
                      <ProfileWithName
                        profileImg={tactic.profileImg || ''}
                        nickname={tactic.nickname || ''}
                        color="#333333"
                        size={36}
                        fontSize={14}
                        borderRadius={8}
                      />
                    </div>
                  </div>

                  {/* 5. 시간 */}
                  <div style={{
                    width: 64,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#666'
                  }}>
                    {(() => {
                      const date = new Date(tactic.createdAt);
                      const now = new Date();
                      const isToday = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
                      return isToday
                        ? `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
                        : `${date.getMonth() + 1}/${date.getDate()}`;
                    })()}
                  </div>

                  {/* 6. 조회수 */}
                  <div style={{
                    width: 72,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingLeft: 8,
                    paddingRight: 12,
                    fontSize: 13,
                    color: '#999',
                    fontWeight: 600
                  }}>
                    {/* 조회수 눈 — 이모지 대신 직접 그림(사용자 2026-10-30 초안 1번). 글자색을 따른다.
                        숫자는 글줄 가운데보다 조금 아래에 앉아 1px 내려 맞춘다(사용자 "높이를 가운데에") */}
                    <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2}
                      strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, flexShrink: 0, transform: 'translateY(1px)' }} aria-hidden="true">
                      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
                      <circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none" />
                    </svg>
                    {tactic.views || 0}
                  </div>
                </div>
              ))}

              {/* 페이지네이션 — 둥근 상자 하나(components/Pager) */}
              <Pager page={currentPage} total={totalPages} onChange={setCurrentPage} />
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* 공략 업로드 모달 */}
      {uploadModalOpen && user && (
        <UploadCounter
          onUpload={async () => {
            await fetchTactics();
            setCurrentPage(1);
            setUploadModalOpen(false);
            setWriteDefense(null);
          }}
          author={user.nickname}
          initialDefense={writeDefense || undefined}
          onClose={() => { setUploadModalOpen(false); setWriteDefense(null); }}
        />
      )}

      {/* 공략 상세 모달 */}
      {selectedTactic && monsterMapping && user && (
        <TacticPost
          open={!!selectedTactic}
          onClose={() => {
            setSelectedTactic(null);
            if (onExternalTacticClose) {
              onExternalTacticClose();
            }
          }}
          tactic={selectedTactic}
          monsterMapping={monsterMapping}
          user={user}
          onTacticDeleted={() => {
            fetchTactics();
            setSelectedTactic(null);
            if (onExternalTacticClose) {
              onExternalTacticClose();
            }
          }}
        />
      )}

      {/* 공략 추가 플로팅 버튼 */}
      <button
        onClick={() => setUploadModalOpen(true)}
        style={{
          position: 'fixed',
          right: 40,
          bottom: 40,
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #8b9eff 0%, #a78bfa 100%)',
          color: '#fff',
          border: 'none',
          fontSize: 28,
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(139, 158, 255, 0.5)',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1) translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(139, 158, 255, 0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1) translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(139, 158, 255, 0.5)';
        }}
        title="공략 추가"
      >
        +
      </button>
    </div>
  );
};

export default Strategy;
